'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslations } from '@repo/core/i18n'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Textarea,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
} from '@repo/ui'
import { SpinnerIosRegular, TextBulletListTreeRegular } from '@fluentui/react-icons'
import { NexusCrm } from '@open/nexus-public-api'
import { toast } from 'sonner'

type FieldDef = NexusCrm.CrmFieldType2
type FieldValue = NexusCrm.CrmFieldValueType
const FieldType = NexusCrm.CrmFieldTypeNative

interface Props {
  customerId: string
  /** Hide chrome when used inline somewhere that already has a card.
   *  Default: render inside a `<Card>` so it can be dropped into a grid. */
  asCard?: boolean
  className?: string
}

/**
 * Customer "Custom fields" panel.
 *
 * Loads field DEFINITIONS via `crm.field.list({target:'customer'})`
 * and current VALUES via `crm.customer.field.value.list({customerId})`,
 * then renders one editable row per field. Edits are persisted via
 * `crm.customer.field.set` with a 600ms debounce per field so a
 * typing burst doesn't hammer the server, but the optimistic UI
 * value is committed immediately.
 *
 * Field-mode `contextTriggered` items are filtered out — they show
 * up at lifecycle moments (e.g. close-interaction form) not on the
 * always-visible profile panel.
 */
export function CustomerCustomFields({ customerId, asCard = true, className }: Props) {
  const t = useTranslations('crm.customerCustomFields')
  const [defs, setDefs] = useState<FieldDef[]>([])
  const [values, setValues] = useState<Map<string, FieldValue>>(new Map())
  const [isLoading, setIsLoading] = useState(true)
  const [savingByField, setSavingByField] = useState<Set<string>>(new Set())

  const getApi = useCallback(async () => {
    const { ServiceRegistry, CrmApiService } = await import('@repo/services')
    return new CrmApiService(ServiceRegistry.getMainService())
  }, [])

  // 1) Load defs + values in parallel.
  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    ;(async () => {
      try {
        const api = await getApi()
        const [defsRes, valuesRes] = await Promise.all([
          api.fieldList({ target: NexusCrm.CrmFieldTargetNative.customer }),
          api.customerFieldValueList({ customerId }),
        ])
        if (cancelled) return
        const activeAlwaysVisible = defsRes.items
          .filter(
            (d) =>
              d.isActive
              && d.mode === NexusCrm.CrmFieldModeNative.alwaysVisible
              && d.type !== FieldType.hidden,
          )
          .sort((a, b) => a.order - b.order)
        setDefs(activeAlwaysVisible)
        const map = new Map<string, FieldValue>()
        for (const v of valuesRes.items) map.set(v.fieldId, v)
        setValues(map)
      } catch (err) {
        console.error('[CustomFields] load failed', err)
        if (!cancelled) toast.error(t('errors.loadFailed'))
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [customerId, getApi, t])

  // 2) Debounced save per fieldId.
  const debouncedSave = useDebouncedSave(getApi, customerId, setSavingByField, setValues, t)

  const onValueChange = useCallback(
    (def: FieldDef, next: { value?: string; values?: string[] }) => {
      // Optimistic local commit.
      setValues((prev) => {
        const m = new Map(prev)
        const existing = m.get(def.id)
        m.set(def.id, {
          ...(existing ?? { id: '', fieldId: def.id }),
          ...next,
        } as FieldValue)
        return m
      })
      debouncedSave(def, next)
    },
    [debouncedSave],
  )

  const content = (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <SpinnerIosRegular className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : defs.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('states.noFields')}</p>
      ) : (
        <div className="space-y-3">
          {defs.map((def) => (
            <FieldRow
              key={def.id}
              def={def}
              value={values.get(def.id)}
              isSaving={savingByField.has(def.id)}
              onChange={(next) => onValueChange(def, next)}
            />
          ))}
        </div>
      )}
    </>
  )

  if (!asCard) {
    return <div className={className}>{content}</div>
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TextBulletListTreeRegular className="h-4 w-4" />
          {t('title')}
        </CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )
}

function FieldRow({
  def,
  value,
  isSaving,
  onChange,
}: {
  def: FieldDef
  value: FieldValue | undefined
  isSaving: boolean
  onChange: (next: { value?: string; values?: string[] }) => void
}) {
  const id = `field-${def.id}`
  const labelEl = (
    <div className="flex items-center gap-2">
      <Label htmlFor={id} className="text-xs font-medium">
        {def.label}
        {def.required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {isSaving && <SpinnerIosRegular className="h-3 w-3 animate-spin text-muted-foreground" />}
    </div>
  )

  switch (def.type) {
    case FieldType.textarea:
      return (
        <div className="space-y-1">
          {labelEl}
          <Textarea
            id={id}
            placeholder={def.placeholder}
            value={value?.value ?? ''}
            onChange={(e) => onChange({ value: e.target.value })}
            rows={3}
          />
        </div>
      )
    case FieldType.checkbox:
      return (
        <div className="flex items-center gap-2">
          <Checkbox
            id={id}
            checked={value?.value === 'true'}
            onCheckedChange={(c) => onChange({ value: c ? 'true' : 'false' })}
          />
          {labelEl}
        </div>
      )
    case FieldType.singleSelect: {
      // A select field with no options renders as a click-to-open
      // empty popover — looks broken (e.g. the "VIP tier" field in
      // the screenshot). Fall back to a disabled hint + an Input so
      // operators can still capture a value even when an admin
      // hasn't seeded the option list yet.
      const opts = def.options ?? []
      if (opts.length === 0) {
        return (
          <div className="space-y-1">
            {labelEl}
            <Input
              id={id}
              value={value?.value ?? ''}
              placeholder={def.placeholder || 'No options configured — type a value'}
              onChange={(e) => onChange({ value: e.target.value })}
            />
          </div>
        )
      }
      return (
        <div className="space-y-1">
          {labelEl}
          <Select
            value={value?.value ?? ''}
            onValueChange={(v) => onChange({ value: v })}
          >
            <SelectTrigger id={id} size="sm">
              <SelectValue placeholder={def.placeholder ?? ''} />
            </SelectTrigger>
            <SelectContent>
              {opts.map((opt) => (
                <SelectItem key={opt.id} value={opt.id}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )
    }
    case FieldType.multiSelect: {
      const selected = new Set(value?.values ?? [])
      return (
        <div className="space-y-1">
          {labelEl}
          <div className="flex flex-wrap gap-1.5">
            {(def.options ?? []).map((opt) => {
              const on = selected.has(opt.id)
              return (
                <Badge
                  key={opt.id}
                  variant={on ? 'default' : 'outline'}
                  className="cursor-pointer select-none"
                  onClick={() => {
                    const next = new Set(selected)
                    if (on) next.delete(opt.id)
                    else next.add(opt.id)
                    onChange({ values: Array.from(next) })
                  }}
                >
                  {opt.label}
                </Badge>
              )
            })}
          </div>
        </div>
      )
    }
    case FieldType.number:
    case FieldType.currency:
    case FieldType.percentage:
    case FieldType.rating:
      return (
        <div className="space-y-1">
          {labelEl}
          <Input
            id={id}
            type="number"
            inputSize="sm"
            placeholder={def.placeholder}
            value={value?.value ?? ''}
            onChange={(e) => onChange({ value: e.target.value })}
          />
        </div>
      )
    case FieldType.date:
    case FieldType.time:
    case FieldType.datetime:
    case FieldType.email:
    case FieldType.phone:
    case FieldType.url:
    case FieldType.text:
    default:
      return (
        <div className="space-y-1">
          {labelEl}
          <Input
            id={id}
            type={
              def.type === FieldType.email
                ? 'email'
                : def.type === FieldType.phone
                  ? 'tel'
                  : def.type === FieldType.url
                    ? 'url'
                    : 'text'
            }
            inputSize="sm"
            placeholder={def.placeholder}
            value={value?.value ?? ''}
            onChange={(e) => onChange({ value: e.target.value })}
          />
        </div>
      )
  }
}

/** Per-field debounced writer so a fast typist sees one save, not 30.
 *  We keep one timer per fieldId. */
function useDebouncedSave(
  getApi: () => Promise<{ fieldValueSetCustomer: (p: NexusCrm.CrmCustomerFieldSetParamsType) => Promise<FieldValue> }>,
  customerId: string,
  setSaving: (updater: (prev: Set<string>) => Set<string>) => void,
  setValues: (updater: (prev: Map<string, FieldValue>) => Map<string, FieldValue>) => void,
  t: (k: string) => string,
) {
  const [timers] = useState(() => new Map<string, ReturnType<typeof setTimeout>>())

  return useCallback(
    (def: FieldDef, payload: { value?: string; values?: string[] }) => {
      const existingTimer = timers.get(def.id)
      if (existingTimer) clearTimeout(existingTimer)
      const handle = setTimeout(async () => {
        timers.delete(def.id)
        setSaving((prev) => {
          const next = new Set(prev)
          next.add(def.id)
          return next
        })
        try {
          const api = await getApi()
          const result = await api.fieldValueSetCustomer({
            customerId,
            fieldId: def.id,
            ...(payload.values !== undefined
              ? { values: payload.values }
              : { value: payload.value ?? '' }),
          })
          setValues((prev) => {
            const m = new Map(prev)
            m.set(def.id, result)
            return m
          })
        } catch (err) {
          console.error('[CustomFields] save failed', err)
          toast.error(t('errors.saveFailed'), {
            description: err instanceof Error ? err.message : String(err),
          })
        } finally {
          setSaving((prev) => {
            const next = new Set(prev)
            next.delete(def.id)
            return next
          })
        }
      }, 600)
      timers.set(def.id, handle)
    },
    [customerId, getApi, setSaving, setValues, t, timers],
  )
}

